import type { SocketType } from './socket'
import type { Widget } from './widget'

/**
 * 节点分类的**标识**。
 *
 * ⚠️ 它原来是一个联合类型
 * （`document | page | data | function | component | container | utility`）。
 * 那是**把领域写死在内核里**：每加一个领域（UI 设计器、日期计算…）
 * 都得回来改内核的类型定义，就不叫「可选加载的节点包」了。
 *
 * 现在分类是**包提供的数据**（`node/package.ts` 的 `NodeCategoryDef`），
 * 内核只认标识字符串。显示名从注册表查（`categoryLabel`），
 * 颜色从主题查（未知分类有兜底色）。
 *
 * 代价是丢掉了编译期的穷尽检查 —— 这是「可扩展」必然要付的，
 * 而收益是内核真的不需要知道有哪些领域。
 */
export type NodeCategory = string

export interface SocketDef {
  /** = v5 契约里的 targetHandle / sourceHandle（如 prop.fontSize） */
  id: string
  type: SocketType
  /**
   * **仅当 `type === 'list'` 时有意义**：列表元素的基础类型。
   *
   * 不填表示「元素类型待推导」—— 连接时从另一端补齐（见 resolveItemType），
   * 这就是「列表元素类型自动推导」的落点。
   */
  itemType?: SocketType
  /**
   * **精确连接**：只接受**完全同一个类型**的一侧。
   *
   * ## 默认是"模糊连接"
   *
   * 不写这个字段时按结构化类型的规则来：**共享祖先就能连，传值时裁到那个祖先**
   * （`component` 与 `page` 共享 `layout`，所以默认它们能互接）。
   * 这对"中转 / 分组"这类节点是对的 —— 它们本来就只关心公共那部分。
   *
   * ## 什么时候该标精确
   *
   * 端口的**语义是"必须是这个东西"**时。打印包的例子：
   *   · 页面的「组件」行必须是 `component` —— 一张纸不能当组件塞进去；
   *   · 文档的「页面」行必须是 `page` —— 组件也不是一页。
   * 不标的话，从端口拖出去会**高亮一堆不该连的端点**（真机反馈：
   * "页面需要连接一个组件，然后我发现它可以连接的节点端点有点多，
   * 而且它都不符合连接需求"）。
   *
   * ⚠️ `any`（通配）在精确连接下**仍然放行**：它是 reroute / 分组这类
   * 中转端口的显式逃生口，挡掉它会让精确端口在中转链路上彻底不可用。
   */
  strict?: boolean
  /** 已有连线 → 端口实心；输入行连线后还会隐藏其控件 */
  connected?: boolean
}

/* ==========================================================================
   行（NodeRow）
   —— 节点内容的唯一排版基元，按 kind 区分六种形态。
   —— 硬约束：**输入与输出不能在同一行**。
   ========================================================================== */

export type NodeRowKind =
  | 'output'
  | 'property'
  | 'input'
  | 'repeater'
  | 'group'
  | 'display'

/**
 * 输出点行 —— 端口在**右**，标签靠右。
 */
export interface OutputRow {
  kind: 'output'
  id: string
  label?: string
  socket: SocketDef
  /**
   * **循环节点专用**：这一路输出在某一轮没值时怎么办。
   *
   *   true（默认）跳过   → 得到真实的 list，但各 list 长度可能不同
   *   false       占位   → 补一个空位，各 list 长度一致、能按行对应
   *
   * 行级而不是节点级：用户要的是「哪一路需要对齐就关哪一路」。
   * 代价是各 list 长度**可以不同** —— 那是刻意的，不是 bug。
   */
  skipEmpty?: boolean
}

/**
 * 属性行 —— 只有内联控件，没有端口。
 */
export interface PropertyRow {
  /** 见 RowConstraint */
  constraint?: RowConstraint
  kind: 'property'
  id: string
  label?: string
  widget: Widget
  /**
   * **只在属性面板里编辑，节点上不画这一行**。
   *
   * 用途是"值是文档数据、但控件不属于画布"的那些参数。今天的例子是
   * JS 节点的 `ports` / `code`（用户原话："JS 代码节点不要显示代码，
   * 这个参数在节点上也不要显示端口的信息，就只显示有哪些输入输出端口就行"）。
   *
   * ⚠️ **行本身照样在 `rows` 里**，只是不渲染：值是存在控件值里的
   * （面板按 rowId 读写），删掉行等于删掉数据。
   */
  panelOnly?: boolean
}

/**
 * 输入行 —— 左端口 + 内联控件。本质上就是「带输入点的属性行」。
 * 一旦该 socket 被连线，控件自动隐藏。
 */
/**
 * 行的校验约束。
 *
 * 值域默认从 `NumberWidget.min/max` 取 —— 那是控件的拖拽边界，
 * 顺便当校验范围最自然（用户拖不到的地方，就是不该有的值）。
 * 这里只需要声明控件表达不了的那些。
 *
 * ⚠️ 数值控件的拖拽**会夹紧**，所以值域越界基本只会在
 * **从外部载入模板**（存档、别的工具生成）时出现 —— 那正是要防的场景。
 */
export interface RowConstraint {
  /** 覆盖控件上的值域 */
  min?: number
  max?: number
  /**
   * 其它规则。返回文案即视为不通过。
   *
   * 用来表达值域说不清的东西，比如「条码窄条宽度必须是 1–10 dots」
   * 这种跟单位、跟具体设备耦合的约束。
   */
  check?: (value: unknown) => string | undefined
  /** 自定义规则不通过时的严重级别，默认 'warning' */
  severity?: DiagnosticSeverity
}

export interface InputRow {
  kind: 'input'
  id: string
  label?: string
  socket: SocketDef
  widget?: Widget
  constraint?: RowConstraint
}

/**
 * 可变输入行（变参列表）—— 一组**同类型**的输入端口，整体作为一个 list 供节点使用。
 *
 * 渲染时会展开成 N 个输入行，但**在数据上它是一个 `list<socketType>`**，
 * 而不是 N 个彼此无关的值。典型用途：or / and 的多个条件、concat 的多个片段。
 *
 * 实例数量不直接存，而是由 `connected` 推导（见 repeaterCount）：
 * 最后一个已连接实例之后始终保留一个空行，供下一次连线 —— 这就是「连了自动加一行」。
 */
/**
 * 变参行是**输入**还是**输出**。
 *
 * ⚠️ 缺省（`undefined`）一律当 `'input'` —— 老数据里没有这个字段，
 * 而所有老数据都是输入方向。
 */
export type RepeaterSide = 'input' | 'output'

export interface RepeaterRow {
  kind: 'repeater'
  id: string
  label?: string
    /**
   * 变参行的**方向** —— 默认 `'input'`（一组输入，数量可增）。
   *
   * ★ `'output'` 是后来加的**通用能力**：一组同类型输出，数量可增。
   * 它和输入方向有两处**必须**不同的语义（都写在 `rows.ts` 的实现里）：
   *
   *   ① **不留空槽**：输入方向末尾那个空槽是「连下一路」用的；
   *      输出方向的空槽没有任何意义（值由节点自己算），留着只会在
   *      每个节点上凭空长出空端口 —— 正是契约 §5 反复警告的那类 bug。
   *   ② **实例类型不由下游推导**：输入方向的实例类型跟着**上游**确认；
   *      输出方向的类型是节点**声明**的（和普通输出行一样），
   *      让下游改它是错的。
   *
   * ⚠️ 契约原本写着「变参行只有输入方向」（理由：输出数量推不出来）。
   * 那条理由对**普通节点**成立，对**接口节点**不成立 ——
   * 接口的输出数量由接口的**另一侧**推导（内层接了几路结果）。
   */
  side?: RepeaterSide
  /**
   * 实例端口是否**精确连接**（见 `SocketDef.strict`）。
   *
   * ⚠️ 变参行上必须能标：打印包的「组件」/「页面」两行就是"只收这一种东西"，
   * 不标的话从那一行拖出去会高亮一堆共享祖先的端点（真机反馈）。
   */
  strict?: boolean
  /** 每个实例端口的基础类型（所有实例相同） */
  socketType: SocketType
  /**
   * handle id 模板，`{i}` 会替换为序号。
   * 默认 `${id}.{i}` → `args.0` / `args.1` / …
   */
  handlePattern?: string
  /** 各实例的连接态，按序号；缺失视为未连接 */
  connected?: boolean[]
  /**
   * 各实例的**自动标签**，按序号 —— 来自上游那个端口的名字。
   *
   * 「监听 0 / 监听 1」这种名字在接上线之后就没用了；
   * 接上「组件 · 文本 · 内容」之后，这一行该显示的就是那个。
   *
   * ⚠️ 和 `connected` 一样是**视图状态**（由边同步而来），
   * 不是用户填的值 —— 别把它当真相用。
   */
  labels?: (string | undefined)[]
  /**
   * 用户给各实例起的名字，按序号。
   *
   * 优先级：`names[i]` → `labels[i]`（自动跟随上游）→ `row.label`（占位名）。
   *
   * ⚠️ 名字是**外观**，端口的身份是 `id` + 序号 —— 运行端按 id/index
   * 识别端口，不认名字。所以改名字不影响输出结果。
   */
  names?: (string | undefined)[]
  /**
   * 按实例记录的类型，覆盖 `socketType`。
   *
   * 为什么需要：变参行只有一个 `socketType`，而**组 / 循环的输入**
   * 恰恰是「每路类型都可能不同」的 —— 外部没连时应该通配（`any`），
   * 连上之后就该**确认成上游那个类型**，否则这一路永远是 `any`，
   * 类型检查形同虚设。
   *
   * ⚠️ 和 `labels` 一样是**派生**的视图状态（由边算出来），不是用户填的。
   */
  instanceTypes?: (SocketType | undefined)[]
  /** 至少保留几个实例（即使都没连接），默认 1 */
  min?: number
  /**
   * **显式**实例数。
   *
   * 不填时实例数由边推导（「最后一个已连实例 + 1 个空槽」）——
   * 那是输入端语义，也是绝大多数变参行的用法。
   *
   * 填了就以此为准（仍然不小于 `min`、不大于 `max`）。
   * 用途：用户点「＋」想先摆几个空行等着接线，
   * 而空行是推不出来的（没有边）。
   */
  count?: number
  /** 上限，防止无限增长；不填 = 不限 */
  max?: number
  /**
   * 这一行的件数**靠接线长**（末尾额外留一个空槽当接线入口）。
   *
   * 只有**组的接口节点**用（`groupInputRows` / `groupOutputRows`）：
   * 它们的件数由「里面接了几路」推，而**接线需要先有一个空位** ——
   * 没有它就是个死锁：`0 个端口 → 没地方接 → 永远 0 个`。
   *
   * ⚠️ **为什么不复用 `min`**：输入方向的变参行，「末尾留空槽」是
   * `repeaterCountFor` 内置的（`lastConnected + 2`），所以 `min` 只表示
   * 「至少渲染几行」。而输出方向**没有** `lastConnected` 可推，
   * 于是「声明数」和「声明数 + 空槽」在 `min` 上**分不开** ——
   * 猜错的后果是外层凭空多一个端口（这个仓库踩过三次）。
   * 所以做成一个**显式**字段：想留空槽就写出来，不靠约定。
   */
  freeSlot?: boolean
}

/**
 * 分组行 —— 无端口、无控件，可折叠的区段标题。
 * 归组靠 `match` 正则匹配其他行的 `id`，不靠逐行标记。
 */
export interface GroupRow {
  kind: 'group'
  id: string
  label: string
  /** 匹配成员行的 id 的正则，例：`^prop\.(fontSize|bold)$` */
  match: string
  /**
   * 运行时状态：现在收起了吗。
   *
   * ⚠️ 它是**状态**不是**默认值**（用户点一下三角就会翻它）。
   * 默认值看 `expanded` —— 两个字段不能合并成一个，否则"用户收起过"
   * 和"这个分组本来就该收起"分不出来（换台机器 / 撤销之后会自己弹开）。
   */
  collapsed?: boolean
  /**
   * **默认展开吗？**不写 = **收起**。
   *
   * 节点高度直接决定拖拽 / 连线的手感：一个参数多的节点（打印包的条码有
   * 十来行）全部铺开会比屏幕还高。所以默认收起，只有"一放下来就要改"的
   * 那一组（比如打印包的「内容」）才显式 `expanded: true`。
   */
  expanded?: boolean
}

/**
 * 展示行 —— 纯展示区，高度可自定。
 */
export interface DisplayRow {
  kind: 'display'
  id: string
  label?: string
  render?: string
  height?: number
}

export type NodeRow =
  | OutputRow
  | PropertyRow
  | InputRow
  | RepeaterRow
  | GroupRow
  | DisplayRow

/* ── 行类型收窄工具 ─────────────────────────────────────────── */

/** 带单一端口的行（不含变参行 —— 变参行的端口是推导出来的） */
export function isSocketRow(row: NodeRow): row is InputRow | OutputRow {
  return row.kind === 'input' || row.kind === 'output'
}

export function rowSocket(row: NodeRow): SocketDef | undefined {
  return isSocketRow(row) ? row.socket : undefined
}

/** 带内联控件的行（属性行 / 输入行） */
export function rowWidget(row: NodeRow): Widget | undefined {
  return row.kind === 'property' || row.kind === 'input' ? row.widget : undefined
}

/* ── 节点级诊断 ─────────────────────────────────────────────── */

export type DiagnosticSeverity = 'error' | 'warning' | 'info'

/**
 * 节点级诊断 —— 由求值层产出，视觉层只关心「严重级别 + 消息」。
 *
 * 刻意定义在 types 里而不是 evaluate 里：这样视觉层能显示徽标，
 * 却不需要依赖求值层。
 */
export interface NodeDiagnostic {
  severity: DiagnosticSeverity
  message: string
}

/* ── 节点 ───────────────────────────────────────────────────── */

/** 框节点的尺寸约束与默认值。库和应用共用一份，避免两边飘 */
export const MIN_FRAME_WIDTH = 140
export const MIN_FRAME_HEIGHT = 100
export const DEFAULT_FRAME_WIDTH = 280
export const DEFAULT_FRAME_HEIGHT = 180

/**
 * 编辑器专用节点（框 / 注释）。
 *
 * 求值层要据此**跳过**它们 —— 不跳过的话会报「未注册求值器」的警告，
 * 一个纯装饰的框上挂个告警徽标，看着像坏了。
 */
export function isEditorOnly(model: Pick<GraphNodeModel, 'editorOnly'>): boolean {
  return Boolean(model.editorOnly)
}

/* ══════════════════════════════════════════════════════════════
   功能数据 / 设计器数据
   ══════════════════════════════════════════════════════════════

   序列化时要能**机械地**丢掉设计器数据（运行端不看那些）。
   靠字段白名单不行 —— 每加一个字段就得记着补一条，迟早漏。

   所以做成**结构性的**：模型 = 功能 + 设计，加字段时编译器会逼你
   归到其中一边。再配一个运行时断言（测试里）保证两张键表和类型没走偏。

   分界线就一条：**改了这个字段，输出结果会不会变？**
   （`muted` 会 → 功能数据；`width` 不会 → 设计器数据）
*/

/**
 * 子图 —— 节点内部还有一张图（组 / 循环）。
 *
 * ## 为什么节点的位置在这里、却不是功能数据
 *
 * 内层节点在顶层画布上看不见，所以它们的坐标只能跟着子图走。
 * 但坐标本身仍然是**设计器数据** —— 序列化时会拆进 `design`（见 graph/serialization.ts）。
 * 这里是**内存形态**：求值只读 `nodes` + `edges`，方便直接喂给 `evaluateGraph`。
 *
 * ## id 只在**本层**唯一
 *
 * 不同层出现同名 id 是允许的（它们是两张独立的图）。所以：
 *   · 求值的记忆表是**每层一份**，不跨层共享
 *   · 校验时内层的问题要**归到包含它的那个节点上** ——
 *     徽标挂在顶层可见的节点上，内层 id 在外面对不上
 */
export interface SubGraph {
  nodes: GraphNodeModel[]
  edges: SubGraphEdge[]
  /** 内层节点 id → 位置（设计器数据，进入子图编辑时用） */
  positions?: Record<string, { x: number; y: number }>
}

/** 子图里的一条边（跟顶层同一套形状，只是不重复定义） */
export interface SubGraphEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
  muted?: boolean
}

/** 运行端需要的数据 —— 求值所必需的最小集 */
export interface FunctionalNode {
  id: string
  /** 节点类型 id，对应 v5 契约的 node.type（如 'component.text'） */
  type: string
  rows: NodeRow[]
  /** 静音：输出视为 NULL，下游回退内联默认值。**会改变求值结果**，所以是功能数据 */
  muted?: boolean
  /**
   * 内部子图（组 / 循环节点才有）。
   *
   * 它**是功能数据** —— 里面装的是真正参与求值的节点，
   * 运行端必须拿到它（IR 会嵌套地带上）。
   */
  graph?: SubGraph
}

/** 只有设计器关心 —— 运行端整个丢掉 */
export interface DesignNode {
  /** 类型自带的显示名 */
  title: string
  /**
   * 自定义名称。设了就覆盖 title 的显示。
   *
   * 入口放在属性面板里，而不是双击节点头部内联编辑 ——
   * Blender 也是这样（它在侧栏的 `Sidebar → Node → Label`）。
   */
  label?: string
  category: NodeCategory
  /**
   * 渲染形态。
   *
   *   'default'  常规节点（header + 行）
   *   'dot'      一个小圆点，只有左右两个端口（接通 / 重路由节点）
   *   'frame'    可拖动的矩形（框节点）
   *   'note'     标题栏 + 一整块可编辑正文（注释节点）
   *
   * ⚠️ 这里刻意用**形态**而不是节点类型来判断 ——
   * 库不该知道 'reroute' 是什么，那是集成方的领域知识。
   */
  render?: 'default' | 'dot' | 'frame' | 'note'
  /**
   * **编辑器专用**：不参与求值，也不该报「未注册求值器」。
   *
   * ⚠️ 跟 `render` 是**两个正交的**概念，别合并：
   *   · 接通节点是 `render: 'dot'`，但它是**要**求值的（直通）
   *   · 框和注释是 `render: 'frame' / 'note'`，**不**求值
   * 合并的话，以后想做「不求值的圆点」或者「会求值的框」就没地方表达了。
   */
  editorOnly?: boolean
  /** 节点宽度 px，默认由 --np-node-width 决定 */
  width?: number
  /** 节点高度 px。**只有框和注释需要**（普通节点高度自适应内容） */
  height?: number
  /** 注释节点的正文（多行） */
  note?: string
  /**
   * 所属框的 id。
   *
   * 归属是**存下来的**，不是每次按几何重算：重算会让跨边界的节点来回抖，
   * 而且挪一下框，里面的节点就「掉出去」了。
   */
  frameId?: string
  /**
   * 隐藏未连接的端口（Blender 的 Ctrl+H）。
   *
   * 只藏 input / output 行 —— 变参行是「增删端口」的容器，藏掉就没地方接线了。
   */
  hideUnused?: boolean
  /**
   * ★ **叠放次序**（越大越靠上）—— 用户 2027 要的那条：
   *
   * > 节点的层级，选中节点应该默认到最顶，**不需要再回到原来的层级**。
   *
   * 上一版的层级是**从选中态现算**的（`selected ? 1 : 0`），于是取消选中
   * 就掉回原来那层 —— 用户看到的是"点一下浮上来、点别处又沉下去"。
   * 现在它是一个**记下来的数**：选中时被赋成 `max(z) + 1`，之后就一直在上面。
   *
   * ⚠️ 归类：**设计器数据**（`DESIGN_KEYS`）。改它不改变任何输出值 ——
   * 运行端拿到 IR 之后叠放次序由布局本身决定，跟编辑器里的前后遮挡无关。
   * 和 `position` 是同一类东西。
   */
  z?: number
}

/**
 * 节点模型 = 功能数据 + 设计器数据。
 *
 * ⚠️ 不要为了省事把新字段直接写进 `GraphNodeModel` ——
 * 那样它就绕过了分类，序列化时会**静默地**漏进（或漏出）RenderTree。
 * 想清楚它属于哪一边，加到对应的接口里。
 */
export type GraphNodeModel = FunctionalNode & DesignNode
